import { Box, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { GridColDef } from '@mui/x-data-grid-pro';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SortDTO } from '../../api';
import { TrainRecordingFilterDTO, UserPopulatedDTO } from '../../api/generated';
import { RESPONSIVE_DRAWER_HEIGHT } from '../../Constants';
import i18n from '../../i18n';
import { localizedDateFormatter, skipUndefined, useSyncState } from '../../utils';
import { DetailContainerLayout } from '../DetailView/DetailContainerLayout';
import { DetailTitlebar } from '../DetailView/DetailTitlebar';
import { ListIndexNavigation } from '../DetailView/ListIndexNavigation';
import { ListWalkNavigation } from '../DetailView/ListWalkNavigation';
import { ActiveFilterChips } from '../Filter/ActiveFilterChips';
import { CombinedFilterList } from '../Filter/CombinedFilterList';
import { FilterSpec } from '../Filter/common';
import { FilterHeaderLayout } from '../Filter/FilterHeaderLayout';
import { FilterPanelHeader } from '../Filter/FilterPanelHeader';
import { FilterPanelBodyLayout, FilterPanelContainer } from '../Filter/FilterPanelLayout';
import { ResetFiltersButton } from '../Filter/ResetFiltersButton';
import { MenuBar } from '../MenuBar';
import { RecordingsList, RecordingSorting } from './RecordingsList';
import { TrainRecordingsDetails } from './TrainRecordingsDetails';
import { TrainRecording, useTrainRecordingList } from '../../api/queries/trainRecordingList';
import { useTrainRecordingFilterOptions } from '../../api/queries/trainRecordingFilterOptions';

export interface TrainRecordingsProps {
  user: UserPopulatedDTO;
}

export const TrainRecordings = memo(function ({ user }: TrainRecordingsProps): ReactElement {
  const { t } = useTranslation();

  const [filter, setFilter] = useState<TrainRecordingFilterDTO>({});
  const [transientFilter, transientFilterSync, setTransientFilter] = useSyncState<TrainRecordingFilterDTO>({});

  const [recordingsPage, setRecordingsPage] = useState(0);
  const [recordingsSorting, setRecordingsSorting] = useState<SortDTO<TrainRecording>>(defaultSorting);

  const { data: filterOptions } = useTrainRecordingFilterOptions({ jobsiteId: user.preferredJobsite?.id, filter });

  const { data: recordingsList } = useTrainRecordingList({
    jobsiteId: user.preferredJobsite?.id,
    filter,
    pageSize,
    recordingsPage,
    recordingsSorting,
  });

  const [selectedRecordingIdx, setSelectedRecordingIdx] = useState<number | undefined>(undefined);

  const handleRowSelect = useCallback(
    function (recordingId: string | null): void {
      if (recordingsList) {
        const idx = recordingsList.data.findIndex((v) => v.id === recordingId);
        if (idx >= 0) {
          setSelectedRecordingIdx(idx + recordingsPage * pageSize);
          return;
        }
      }
      setSelectedRecordingIdx(undefined);
    },
    [recordingsList, recordingsPage]
  );

  // Memoized value for the ID of the selected recording
  const selectedRecordingId = useMemo(() => {
    if (selectedRecordingIdx !== undefined && !!recordingsList) {
      const indexOnPage = selectedRecordingIdx - recordingsList.offset;
      if (0 <= indexOnPage && indexOnPage < recordingsList.data.length) {
        return recordingsList.data[indexOnPage].id;
      }
    }
    return undefined;
  }, [selectedRecordingIdx, recordingsList]);

  // Effect to change page if selection moves outside current page
  useEffect(
    function movePageToCoverSelection() {
      if (!!recordingsList && selectedRecordingIdx !== undefined) {
        const selectionPage = Math.floor(selectedRecordingIdx / pageSize);
        if (selectionPage !== recordingsPage) {
          setRecordingsPage(selectionPage);
        }
      }
    },
    [selectedRecordingIdx, recordingsPage, recordingsList]
  );

  const detailRecording = useMemo(() => {
    return selectedRecordingId ? recordingsList?.data.find((v) => v.id === selectedRecordingId) : undefined;
  }, [selectedRecordingId, recordingsList]);

  const handleCloseDetails = useCallback(function (): void {
    setSelectedRecordingIdx(undefined);
  }, []);

  const handleSortChange = useCallback(
    function (sort: RecordingSorting | null): void {
      handleCloseDetails();
      setRecordingsSorting(sort ? { sortField: sort.field, sortDirection: sort.sort } : defaultSorting);
    },
    [handleCloseDetails]
  );

  const handleFilterCommit = useCallback(
    function (): void {
      setFilter(transientFilterSync.current);
    },
    [transientFilterSync]
  );

  // Effect to synchronize transient filter when main filter changes
  useEffect(
    function syncTransientFilter(): void {
      setTransientFilter(filter);
    },
    [filter, setTransientFilter]
  );

  const handleRecordingsPageChangeByUser = useCallback(
    function (newPage: number) {
      handleCloseDetails();
      setRecordingsPage(newPage);
    },
    [handleCloseDetails]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
      <Box display={'flex'}>
        <MenuBar />
        {/* Fixed 3-column layout using Grid */}
        <Grid
          container
          columnSpacing={2}
          sx={{
            height: `calc(100% - ${RESPONSIVE_DRAWER_HEIGHT}px)`,
            width: '100%',
            top: RESPONSIVE_DRAWER_HEIGHT,
            position: 'absolute',
            overflow: 'hidden',
          }}
          padding={2}
        >
          {/* Column 1: Filters*/}
          <Grid size={{ md: 3 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Paper elevation={8} sx={{ borderRadius: '10px', height: '100%' }}>
              <FilterPanelContainer>
                <FilterPanelHeader />
                <FilterPanelBodyLayout>
                  <CombinedFilterList
                    filterSpec={recordingsFilterSpec}
                    filter={transientFilter}
                    filterOptions={filterOptions || {}}
                    onFilterChange={setTransientFilter}
                    onCommit={handleFilterCommit}
                  />
                </FilterPanelBodyLayout>
              </FilterPanelContainer>
            </Paper>
          </Grid>

          {/* Column 2: Recordings List*/}
          <Grid
            size={{ md: selectedRecordingIdx === undefined ? 5.5 : 3.5 }}
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Paper
              elevation={8}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                borderRadius: '10px',
                boxSizing: 'border-box',
                padding: 2,
              }}
            >
              <FilterHeaderLayout>
                <ResetFiltersButton filter={filter} onFilterChange={setFilter} />
                <ActiveFilterChips filterSpec={recordingsFilterSpec} filter={filter} onFilterChange={setFilter} />
              </FilterHeaderLayout>
              <RecordingsList
                columns={columns}
                data={recordingsList || null}
                selectedId={selectedRecordingId}
                onSelect={handleRowSelect}
                pageSize={pageSize}
                page={recordingsPage}
                onPageChange={handleRecordingsPageChangeByUser}
                onSortChange={handleSortChange}
              />
            </Paper>
          </Grid>

          {/* Column 3: Details */}
          <Grid size={{ md: selectedRecordingIdx === undefined ? 3.5 : 5.5 }} sx={{ height: '100%' }}>
            {/* Conditional rendering of Detail content based on selection */}
            <DetailContainerLayout elevation={8}>
              {selectedRecordingIdx !== undefined && detailRecording ? (
                <>
                  <DetailTitlebar onClose={handleCloseDetails} title={t('recording.details')}>
                    <Box>
                      <ListIndexNavigation
                        Component={ListWalkNavigation}
                        maxIdx={recordingsList?.total}
                        currentIdx={selectedRecordingIdx}
                        onIndexChange={setSelectedRecordingIdx}
                      />
                    </Box>
                  </DetailTitlebar>
                  <TrainRecordingsDetails key={detailRecording.id} recording={detailRecording!} />
                </>
              ) : (
                <Box display='flex' alignItems='center' justifyContent='center' height='100%'>
                  <Typography variant='body1' textAlign='center'>
                    {t('recording.selectForDetails')}
                  </Typography>
                </Box>
              )}
            </DetailContainerLayout>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
});

const pageSize = 50;
const defaultSorting: SortDTO<TrainRecording> = {
  sortDirection: 'desc',
  sortField: 'motionStartAt',
};

const recordingsFilterSpec: FilterSpec[] = [
  { filterKey: 'motion_start_at', type: 'DateRange', filterProps: {}, displayName: 'recording.motionStartAt' },
];

const columns: GridColDef[] = [
  {
    headerName: 'recording.motionStartAt',
    field: 'motionStartAt',
    resizable: false,
    flex: 0.7,
    type: 'dateTime',
    valueFormatter: localizedDateFormatter,
  },
  {
    headerName: 'recording.wagonNumber',
    field: 'wagonNumber',
    resizable: false,
    sortable: false, // api doesn't support sorting
    flex: 0.5,
    type: 'string',
    valueFormatter: (value) => skipUndefined(value, (v: string) => v.toUpperCase()),
  },
];
